import { Link } from "react-router-dom";
import { MapPin, Clock, TrendingUp, BadgePercent } from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils";
import type { Franchise } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { useToggleSaveListing } from "@/hooks/useSavedListings";

type Props = {
  item: Franchise;
};

const getPrimaryImg = (images: Franchise['images']) => 
  (images?.find((i) => i.isPrimary) ?? images?.[0])?.image?.publicUrl ?? null;

const fmtPrice = (str?: string) => {
  if (!str) return '—';
  const n = parseFloat(str);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} tỷ`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} tr`;
  return formatNumber(n);
};

export default function FranchiseCard({ item }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(item.id);
  const img = getPrimaryImg(item.images);

  // Chỉ hiển thị tag trạng thái cho Seller/Admin. Buyer và Guest không cần thấy.
  const showStatusTag = !!user && (user.role === "seller" || user.role === "admin");

  return (
    <Link
      to={`/nhuong-quyen/${item.id}`}
      className="fnb-card block animate-fade-up h-full flex flex-col relative"
    >
      {/* Thumbnail */}
      <div className="h-44 overflow-hidden rounded-t-xl relative shrink-0 bg-white border-b border-gray-100/50 flex items-center justify-center p-3">
        {img ? (
          <img
            src={img}
            alt={item.name}
            className="max-w-full max-h-full object-contain transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center text-6xl"
            style={{ background: "var(--brand-pale)" }}
          >
            🏪
          </div>
        )}

        <CardStatusTag
          className="absolute top-2 left-2 z-10"
          show={showStatusTag}
          status={item.approvalStatus}
          label={item.approvalStatus === "approved" ? "Đã duyệt" : item.approvalStatus === "rejected" ? "Từ chối" : "Chờ duyệt"}
        />
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        {/* Business type badge */}
        <div className="flex gap-1.5 mb-2 flex-wrap">
          <span className="fnb-tag fnb-tag-brand text-[10px]">
            {item.businessTypeName}
          </span>
          {item.businessSectorName && (
            <span className="fnb-tag text-[10px]">
              {item.businessSectorName}
            </span>
          )}
        </div>

        {/* Title */}
        <h3
          className="font-semibold text-sm leading-snug line-clamp-2 mb-3 pr-8"
          style={{ color: "var(--text-1)" }}
        >
          {item.name}
        </h3>

        {/* Investment required */}
        {(item.investment_required_min || item.investment_required_max) && (
          <p
            className="font-display font-black text-lg mb-3"
            style={{ color: "var(--brand)" }}
          >
            {fmtPrice(item.investment_required_min)}
            {item.investment_required_max && ` – ${fmtPrice(item.investment_required_max)}`}
            <span className="text-xs font-normal ml-1" style={{ color: "var(--text-3)" }}>
              {item.currencyUnit}
            </span>
          </p>
        )}

        {/* Fee pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {item.franchise_fee && (
            <div
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg"
              style={{ background: "var(--brand-dim)", color: "var(--brand)" }}
            >
              <BadgePercent size={10} />
              Phí NQ: {fmtPrice(item.franchise_fee)}
            </div>
          )}
          {item.royalty_fee && (
            <div
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg"
              style={{ background: "var(--surface-2)", color: "var(--text-2)" }}
            >
              <TrendingUp size={10} />
              Royalty: {item.royalty_fee}%
            </div>
          )}
        </div>

        {/* Meta */}
        <div
          className="flex flex-wrap gap-1.5 pt-3 border-t mt-auto pr-10"
          style={{ borderColor: "var(--border-c)" }}
        >
          {item.province?.name && (
            <span className="fnb-tag text-[10px]">
              <MapPin size={9} /> {item.province.name}
            </span>
          )}
          {item.contract_duration && (
            <span className="fnb-tag text-[10px]">
              <Clock size={9} /> HĐ {item.contract_duration} tháng
            </span>
          )}
          {item.profit_margin && (
            <span className="fnb-tag text-[10px]">
              <TrendingUp size={9} /> LN: {item.profit_margin}%
            </span>
          )}
        </div>
      </div>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(item.id, "Franchise");
        }}
      />
    </Link>
  );
}
